import { EXPIRED_CAMPAIGN_PAGE_IDS } from '@/constants/CategoryPageConstants'
import { COLLECTION_ROUTE_NAME } from '@/constants/CollectionPageConstants'
import { SEARCH_V2_ROUTE_NAME } from '@/constants/SearchConstants'
import Query, { DEFAULT_PAGE_SIZE } from '@/modules/category/Query'
import { mapSearchProduct } from '@/modules/product/SearchProductMapper'
import { translate } from '@/plugins/i18n.js'
import SiteConfig from '@/services/SiteConfig'
import { defineStore } from 'pinia'

const VICAMPO_VENDOR_ID = '452'

let debounce
let activeFilters = {}
let queryStash
let inFlightSearchKey = null
// Monotonic id per v2 search; lets a response that arrives after a newer
// search has started detect it is stale (VIP-12032).
let searchGeneration = 0

const NO_SEARCH = 'pagination_page=1'

export const useSearchConfig = defineStore('searchConfig', {
  state: () => ({
    products: [],
    config: {
      category: {
        sort_options: [],
        attributes: [],
        is_enabled: true
      },
      search: '',
      results: [],
      query: {},
      filters: {},
      totalCount: 0,
      totalPages: 0,
      error: false
    },
    loading: false,
    displaySolrData: false,
    categoryIdOverride: null,
    // Manufacturer hits for the global search page's horizontal strip
    // (separate from `config.results`, which feeds the product grid).
    manufacturerHits: []
  }),
  getters: {
    activeAttributes: ({ config: { filters } }) =>
      Object.values(filters)
        .map((filter) => Object.values(filter))
        .flat(),
    activeFilters: ({ config: { filters } }) => Object.keys(filters),
    hasSolrData: ({ displaySolrData }) => displaySolrData,
    categoryIsLoading: ({ loading }) => loading,
    category: ({ config: { category } }) => category,
    getConfig: ({ config }) => config,
    getProducts: ({ products }) => products,
    enableCms: ({ config: { category } }) => category.enable_cms,
    manufacturers: ({ config: { results } }) => {
      const entities =
        results?.filter(({ entity_type: et }) => et === 'manufacturer') ?? []
      // priority if: only 1 manufacturer is present, first in the results (-> the highest rank in score: tested)
      const priority = entities[0] === results?.[0]
      return { entities, priority }
    },
    filterCount: ({ config: { filters } }) =>
      Object.values(filters).reduce(
        (c, filter) => c + Object.keys(filter).length,
        0
      ),
    blocks: ({ config: { category } }) => ({
      before: { html: category.before_html, css: category.before_html_css },
      after: { html: category.after_html, css: category.after_html_css }
    }),
    elbwalkerContext({ config: { category, totalCount } }) {
      const currentPage =
        +this.router.currentRoute.value.query.pagination_page || 1
      const context = [
        'shopping:discover',
        `product_sort:${category.sort}`,
        `product_count:${totalCount}`,
        `product_page:${currentPage}`
      ]
      let filterCount = 0
      Object.entries(this.getConfig.filters).forEach(([attribute, options]) => {
        context.push(`filter_${attribute}:${Object.keys(options).join(',')}`)
        filterCount += Object.keys(options).length
      })
      context.push(`filter_count:${filterCount}`)
      return context
    }
  },
  actions: {
    async fetchManufacturerProducts(manufacturerId) {
      if (!this._beginSearch()) return
      this.config.error = false

      try {
        this.loading = true
        const { ApiController } = await _getModules()
        const [, filters, search] = await _getUrlData(this.router.currentRoute)
        activeFilters = filters

        const res = await ApiController.fetchManufacturerProducts(
          manufacturerId,
          search || NO_SEARCH
        )
        res.data.category.attributes = res.data.category.attributes.map(
          (attr) => ({ ...attr, is_top_filter: true })
        )

        this.config = _getConfig(res, res)

        void this.fetchProducts()
      } catch (e) {
        this.config.error = true
        console.error('Search config fetch failed:', e)
      } finally {
        this.loading = false
        this._endSearch()
      }
    },
    async fetchCategory(categoryIdOverride = null) {
      if (categoryIdOverride) this.categoryIdOverride = categoryIdOverride
      this.config.error = false
      const { ApiController, TrackingHelper } = await _getModules()
      const route = this.router.currentRoute.value

      if (route.name === 'manufacturer') {
        await this.fetchManufacturerProducts(route.params.id)
        return
      }

      if (!this._beginSearch()) return

      const [routeCategoryId, filters, search] = await _getUrlData(
        this.router.currentRoute
      )
      const categoryId = this.categoryIdOverride ?? routeCategoryId
      activeFilters = filters
      const cacheKey = search || NO_SEARCH

      try {
        this.loading = true

        const completedSearch = categoryId
          ? `category_id=${categoryId}&${search}`
          : search
        const res = await ApiController.fetchCategory(completedSearch)

        const redirect = await _safetyCheck(res)

        if (redirect) {
          await this.router.replace(redirect)
          return
        }

        this.config = _getConfig(res, cacheKey)
        void TrackingHelper.sendUtmParams()
        TrackingHelper.sendCategoryPageView(this.category.code)

        void this.fetchProducts()
      } catch (e) {
        this.config.error = true
        console.error('Category fetch failed:', e)
      } finally {
        this._endSearch()
      }
      this.loading = false
    },
    async fetchProducts() {
      const { MetaProduct } = await _getModules()

      // v2 search docs carry the full tile payload (price object, awards,
      // ratings, …) which mapSearchProduct() renames onto MetaProduct fields;
      // legacy docs only yield their minimal subset and keep the bulk fetch.
      this.products =
        this.config.results
          ?.filter((product) => product.entity_type === 'product')
          .map((product) => new MetaProduct(mapSearchProduct(product))) ?? []
    },
    updateCategory({ group, value, checked = undefined, immediate = true }) {
      _setupQuery(checked, group, value, this.router)

      if (!immediate) return

      clearTimeout(debounce)
      // boomer timer <3
      debounce = setTimeout(() => {
        void this.applyFilter()
        debounce = null
      }, 500)
    },
    resetFilter(group = null) {
      _clearQuery(group)
    },
    // De-dup guard shared by every route-driven fetcher. A filter/sort/
    // pagination change pushes the new query AND fetches imperatively
    // (applyFilter), while the page's route watcher — added so browser back/
    // forward reload the page (VIP-11940) — reacts to that same push. Both
    // resolve to an identical request; _beginSearch records the (id + query)
    // key and returns false for the second caller so only one runs. A
    // genuinely different navigation (e.g. the back button) produces a new key
    // and still fetches. Pair every truthy return with _endSearch() in a
    // `finally`.
    _beginSearch() {
      const route = this.router.currentRoute.value
      const key = `${route.params.id ?? ''}?${new URLSearchParams(
        route.query
      ).toString()}`
      if (key === inFlightSearchKey) return false
      inFlightSearchKey = key
      // The pushed query is now the source of truth; drop the filter draft so
      // the next interaction rebuilds it from the (possibly back-navigated)
      // route instead of a stale stash.
      queryStash = null
      return true
    },
    _endSearch() {
      inFlightSearchKey = null
    },
    // Shared scaffold for the v2 product-search fetchers (collection pages,
    // global search): loading/error flags, route parsing into `activeFilters`
    // and v2 params, and product mapping. On failure it sets `config.error`
    // so the page renders its own inline error (VIP-11911). `run` assembles
    // `this.config`; returning false skips the product mapping (used after an
    // in-flight redirect).
    async _runProductSearch(errorLabel, run) {
      if (!this._beginSearch()) return
      const generation = ++searchGeneration
      // Once a newer search has started, this one must neither publish its
      // response nor touch the shared loading/error flags (VIP-12032).
      const isStale = () => generation !== searchGeneration
      this.config.error = false
      this.loading = true
      try {
        const modules = await _getModules()

        const [, filters] = await _getUrlData(this.router.currentRoute)
        activeFilters = filters

        const params = new Query(this.router.currentRoute.value).toV2Params()

        if ((await run({ modules, params, isStale })) === false) return

        void this.fetchProducts()
      } catch (e) {
        if (isStale()) return
        this.config.error = true
        // VIP-11911: surface fetch failures inline (config.error → the page's
        // own error message) instead of redirecting to /error.
        console.error(`${errorLabel}:`, e)
      } finally {
        if (!isStale()) {
          this.loading = false
          this._endSearch()
        }
      }
    },
    async fetchCollectionPageProducts(collectionPageId) {
      await this._runProductSearch(
        'Collection page products fetch failed',
        async ({ modules, params, isStale }) => {
          const { ApiController, useCollectionPageStore } = modules

          // `products` is a group ({total, items, attributes}) — same shape as
          // the products group of the global search endpoint.
          const {
            collection_page,
            products,
            size,
            hero_product,
            redirect_url: redirectUrl,
            redirect_preview: redirectPreview = null
          } = await ApiController.fetchCollectionPageProducts(
            collectionPageId,
            params
          )

          if (isStale()) return false

          // `?? {}` tolerates a null/missing products group (VIP-11916); the
          // destructuring defaults below only cover `undefined`.
          const {
            total = 0,
            items = [],
            attributes: rawAttributes = []
          } = products ?? {}

          // The API returns redirect_url only when a configured redirect condition
          // matches (page disabled / campaign expired) and the visitor is not an admin.
          const redirectTarget = redirectUrl?.replace(/https?:\/\/[^/]+/, '')
          if (
            redirectTarget &&
            redirectTarget !== this.router.currentRoute.value.fullPath
          ) {
            await this.router.replace(redirectTarget)
            return false
          }

          // The collection route `/:prefix*-col-c:id(\\d+):suffix?` matches any prefix,
          // so visitors can reach the page under a non-canonical URL. Snap the URL bar
          // to the canonical form (page_url + `-col-c<id>`, or just `/-col-c<id>` when
          // no slug is set) while preserving the query. The API returns page_url without
          // the suffix (e.g. "full-example"), so we always append it here.
          // Compare on `path` so query-only changes don't loop.
          const canonicalSlug = (collection_page?.page_url || '')
            .replace(/^\//, '')
            .replace(/-col-c\d+$/, '')
          const canonicalPath = canonicalSlug
            ? `/${canonicalSlug}-col-c${collectionPageId}`
            : `/-col-c${collectionPageId}`
          if (canonicalPath !== this.router.currentRoute.value.path) {
            await this.router.replace({
              path: canonicalPath,
              query: this.router.currentRoute.value.query
            })
          }

          const attributes = _mapAttributes(rawAttributes)

          const { sort_options, activeSortValue } = _mapSortOptions(
            collection_page.sort_options,
            collection_page.default_sort ?? 'popularity',
            collection_page.default_sort_direction ?? null
          )

          const hero_data = _heroData(hero_product, collection_page)

          const filterMap = attributes.reduce(
            (acc, attr) => _activeFilterReducer(acc, attr),
            {}
          )

          this.config = _buildProductSearchConfig({
            category: {
              ...collection_page,
              sort_options,
              // FE-encoded selection (e.g. `price_desc`) so the dropdown can seed
              // itself directly without re-composing key + direction.
              default_sort: activeSortValue,
              hero_data,
              attributes,
              is_enabled: true,
              code: collection_page.page_url ?? String(collectionPageId)
            },
            // Guard against a null items list (VIP-11916) — the destructuring
            // default only covers `undefined`, so a `null` would otherwise crash.
            // The BE excludes the hero product from the items itself (VIP-12118),
            // so no client-side de-duplication is needed here.
            results: _stampProducts(Array.isArray(items) ? items : []),
            filters: filterMap,
            total,
            size
          })

          const collectionPageStore = useCollectionPageStore()
          collectionPageStore.setFromProductsSearch(
            collection_page,
            redirectPreview
          )
        }
      )
    },
    async fetchGlobalSearch() {
      await this._runProductSearch(
        'Global search fetch failed',
        async ({ modules, params, isStale }) => {
          const { ApiController } = modules

          const {
            q,
            size,
            sort_options: rawSortOptions = [],
            sort,
            direction,
            products,
            manufacturers,
            redirect_url: redirectUrl
          } = await ApiController.fetchGlobalSearch(params)

          if (isStale()) return false

          // The API returns redirect_url only when the query matches a product SKU
          // (→ PDP) or a configured search rewrite (→ canonical category URL). Strip
          // the host and ensure a leading slash so it navigates as an absolute path
          // instead of resolving against the current /search/v2 route (which would
          // prefix it with `search/`).
          let redirectTarget = redirectUrl?.replace(/https?:\/\/[^/]+/, '')
          if (redirectTarget && !redirectTarget.startsWith('/')) {
            redirectTarget = `/${redirectTarget}`
          }
          if (
            redirectTarget &&
            redirectTarget !== this.router.currentRoute.value.fullPath
          ) {
            await this.router.replace(redirectTarget)
            return false
          }

          const attributes = _mapAttributes(products?.attributes ?? [])

          const { sort_options, activeSortValue } = _mapSortOptions(
            rawSortOptions,
            sort ?? 'popularity',
            direction ?? null
          )

          // Manufacturer hits render as a horizontal strip on the search page,
          // not as tiles inside the product grid — keep them out of `results`.
          this.manufacturerHits = manufacturers?.items ?? []

          this.config = _buildProductSearchConfig({
            category: {
              title: '',
              q,
              sort_options,
              // FE-encoded selection so the dropdown can seed itself directly.
              default_sort: activeSortValue,
              attributes,
              is_enabled: true,
              code: 'search-v2'
            },
            results: _stampProducts(products?.items ?? []),
            filters: attributes.reduce(
              (acc, attr) => _activeFilterReducer(acc, attr),
              {}
            ),
            total: products?.total ?? 0,
            size
          })
        }
      )
    },
    // Single dispatch point for "re-fetch whatever the current route shows".
    // Every refresh trigger (filter change, pagination, supplier switch) must
    // go through here so a new page type only registers its fetcher once.
    async fetchForCurrentRoute() {
      const route = this.router.currentRoute.value
      if (route.name === COLLECTION_ROUTE_NAME) {
        await this.fetchCollectionPageProducts(route.params.id)
      } else if (route.name === SEARCH_V2_ROUTE_NAME) {
        await this.fetchGlobalSearch()
      } else {
        await this.fetchCategory()
      }
    },
    async applyFilter() {
      try {
        if (!(queryStash instanceof Query)) {
          queryStash = new Query(this.router.currentRoute.value)
        }
        await this.router.push({ query: queryStash.getSearchParams() })
        void this.fetchForCurrentRoute()
      } catch (_) {
        // changing filters back and forth could lead to duplicate navigation error
      }
    },
    resetConfig() {
      this.$reset()
      clearTimeout(debounce)
      debounce = null
      queryStash = null
      activeFilters = {}
      inFlightSearchKey = null
    }
  }
})

// Full tile payload from the v2 search doc; HeroTile renders straight from
// it and skips the bulk fetch when the structured price object is present.
const _heroData = (heroProduct, collectionPage) => {
  if (!heroProduct) return null

  const mapped = mapSearchProduct(heroProduct)
  // The collection hero catch phrase wins, but don't clobber the doc's own
  // catch_phrase when the page has none.
  if (collectionPage.hero_catch_phrase) {
    mapped.catch_phrase = collectionPage.hero_catch_phrase
  }
  return mapped
}

const _getUrlData = async (currentRoute) => {
  const params = currentRoute.value.query
  const filters = Object.keys(params).reduce(
    (filters, key) => ({
      ...filters,
      [key]: (params[key] + '').split(';')
    }),
    {}
  )

  const search = new URLSearchParams(params).toString().replaceAll('%3B', ';')

  return [currentRoute.value.params.id, filters, search]
}

/**
 * collect filters by clustering them: { attribute: { activeOptionName: { ...activeOption }}}
 */
const _activeFilterReducer = (
  cur,
  { code: group, name: groupLabel, options }
) => {
  const activeAttrs = (activeFilters[group] ?? []).filter((v) => v !== '')
  if (!activeAttrs.length) return cur

  // Build the active options from the selected URL values directly so every
  // applied value on this facet is represented — including one the aggregation
  // didn't return a bucket for (e.g. scoped out by the other active filters),
  // which previously produced no chip and left the panel closed. Fall back to
  // the raw value as the label when no matching option is present.
  const optionLabels = new Map(
    options.map(({ name, label }) => [String(name), label])
  )
  const activeOpts = activeAttrs.reduce((acc, value) => {
    const name = String(value)
    return {
      ...acc,
      [name]: { groupLabel, group, name, label: optionLabels.get(name) ?? name }
    }
  }, {})

  return { ...cur, [group]: activeOpts }
}

// Both v2 fetchers stamp grid products identically so `fetchProducts` and the
// `manufacturers` getter can tell entity types apart.
const _stampProducts = (products) =>
  products.map((p) => ({ ...p, entity_type: 'product' }))

// Common shell of the config object the v2 product-search fetchers produce.
// `query` stays an empty placeholder (the legacy category flow fills it with
// the corrected/original search term); nothing reads a `search` field, so the
// shell deliberately has none.
const _buildProductSearchConfig = ({
  category,
  results,
  filters,
  total,
  size
}) => ({
  category,
  results,
  filters,
  totalCount: total,
  totalPages: Math.ceil(total / (size || DEFAULT_PAGE_SIZE)),
  query: {},
  error: false
})

/**
 * Maps raw BE facet attributes to the FE filter shape: every attribute becomes
 * a multi-selection top filter, option `count` becomes `results`, and vendor
 * options collapse to the VICAMPO/Marketplace labels.
 */
// Sidebar facet order of the legacy search (Anbieter, Weinart, Preis, Land,
// Region, Rebsorte, …); codes missing here keep the BE order behind these.
const FILTER_ORDER = [
  'vendor',
  'wine_type',
  'price_bracket',
  'country',
  'region',
  'wine_grape_variety',
  'customer_rating',
  'taste_cluster',
  'manufacturer_id',
  'wine_year',
  'wine_quality',
  'vdp_classification'
]
// The first three facets start expanded, like in the legacy search.
const OPEN_FILTERS = new Set(['vendor', 'wine_type', 'price_bracket'])

const _filterPosition = ({ code }) => {
  const index = FILTER_ORDER.indexOf(code)
  return index === -1 ? FILTER_ORDER.length : index
}

const _mapAttributes = (rawAttributes = []) =>
  rawAttributes
    .map((attr) => ({
      type: 'multi-selection',
      ...attr,
      is_top_filter: true,
      open_on_show: OPEN_FILTERS.has(attr.code),
      options:
        attr.code === 'vendor'
          ? attr.options.map(({ name, count }) => ({
              name: String(name),
              label:
                String(name) === VICAMPO_VENDOR_ID ? 'VICAMPO' : 'Marketplace',
              results: count
            }))
          : attr.options.map(({ count, ...opt }) => ({
              ...opt,
              results: count
            }))
    }))
    .sort((a, b) => _filterPosition(a) - _filterPosition(b))

/**
 * sort_options from BE is `[{ key, allowed_directions }]`. Each entry expands
 * to one dropdown item per direction; the value encodes the direction (suffix
 * `_desc` for the non-default direction of multi-direction keys, otherwise
 * just the key). Returns the dropdown catalog plus the FE-encoded active value.
 */
const _mapSortOptions = (
  rawSortOptions,
  activeSortKey,
  activeSortDirection
) => {
  const expanded = (rawSortOptions ?? []).flatMap(
    ({ key, allowed_directions }) =>
      allowed_directions.map((direction) => {
        const value = _composeSortValue(key, direction, allowed_directions)
        return { value, label: translate('sort_option_' + value) }
      })
  )
  const activeSortValue = _composeSortValue(
    activeSortKey,
    activeSortDirection,
    // No catalog entry available for this fallback path — assume the key's
    // active direction is its first allowed; that matches BE behaviour.
    activeSortDirection ? [activeSortDirection] : []
  )
  const sort_options =
    expanded.length > 0
      ? expanded
      : [
          {
            value: activeSortValue,
            label: translate('sort_option_' + activeSortValue)
          }
        ]

  return { sort_options, activeSortValue }
}

// Encodes a (key, direction) pair as the single-string dropdown value the
// rest of the FE expects. For multi-direction keys (e.g. `price`, `name`)
// the non-default direction gets a `_<direction>` suffix; everything else
// uses the bare key. The default direction is `allowed_directions[0]`,
// matching the BE's behaviour when no `direction` is provided.
const _composeSortValue = (key, direction, allowedDirections) => {
  if (!key) return key
  if (allowedDirections.length <= 1) return key
  const defaultDirection = allowedDirections[0]
  return direction && direction !== defaultDirection
    ? `${key}_${direction}`
    : key
}

const _setupQuery = (checked, group, value, router) => {
  if (!(queryStash instanceof Query)) {
    queryStash = new Query(router.currentRoute.value)
  }
  queryStash.setPaginationPage(1)
  if (checked !== undefined) {
    checked
      ? queryStash.setSearchParam(group, value, true)
      : queryStash.removeSearchParam(group, value)
  } else {
    queryStash.setSearchParam(group, value)
  }
}

const _clearQuery = (group) => {
  if (!(queryStash instanceof Query)) {
    queryStash = new Query()
  }
  group ? queryStash.removeSearchParam(group) : queryStash.clearSearchParams()
}

const _getConfig = ({ data, headers }, search) => {
  const totalCount = +headers['x-total-count']
  const totalPages = +headers['x-total-pages']
  const filters = data.category.attributes.reduce(
    (acc, attr) => _activeFilterReducer(acc, attr),
    {}
  )
  const rewrite = data.rewrite
  const error = false

  return { ...data, totalCount, totalPages, search, filters, rewrite, error }
}

const _safetyCheck = async ({ data }) => {
  const redirect = data.redirect_url
  if (redirect) return redirect

  const isEnabled = data?.is_enabled ?? data?.category?.is_enabled
  if (isEnabled) return null

  const { customerStore } = await _getModules()
  await customerStore.waitUntilPending()

  if (customerStore.customerIsAdmin) return null

  const pageId = EXPIRED_CAMPAIGN_PAGE_IDS[SiteConfig.locale]
  if (pageId) {
    return `/kampagne-abgelaufen-cms-p${pageId}`
  }

  return { name: 'home' }
}

const _getModules = (() => {
  let cache

  return async () => {
    if (cache) return cache

    const [
      { default: ApiController },
      { default: TrackingHelper },
      { default: MetaProduct },
      { useCustomerStore },
      { useCollectionPageStore }
    ] = await Promise.all([
      import('@/services/ApiController'),
      import('@/services/helpers/TrackingHelper'),
      import('@/modules/product/MetaProduct'),
      import('@/stores/customer'),
      import('@/stores/collectionPage')
    ])
    const customerStore = useCustomerStore()
    cache = {
      ApiController,
      TrackingHelper,
      MetaProduct,
      customerStore,
      useCollectionPageStore
    }
    return cache
  }
})()
